import React, { useEffect, useMemo, useState } from "react";

import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import EnhancedTable from "../table/Table";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { displayDateFormart, isValidArr } from "../../utility/utils";
import { fetchUserHierarchys } from "../../redux/actions/UserHierarchyActions";
import { fetchUsers } from "../../redux/actions/UserActions";
import { fetchSBUs } from "../../redux/actions/SBUActions";
import { fetchSBGs } from "../../redux/actions/SBGActions";
import { fetchCSGOs } from "../../redux/actions/CSGOActions";
import Loader from "../../components/loader/Loader";
import { UserHierarchyModel } from "../../models/UserHierarchyModel";
import UserHierarchyForm from "./UserHierarchyForm";

export type Mode = "add" | "delete";

type iData = {
  id: string;
  bdpoName: string;
  sbuName: string;
  sbgName: string;
  csgoName: string;
  createdDate: string;
  // status: string;
};

const UserHierarchyPage = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<UserHierarchyModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);

  const allUserHierarchys = useAppSelector(
    (state) => state.UserHierarchy.all_user_hierarchys
  );
  const allUsers = useAppSelector((state) => state.user.all_Users);
  const allSBUs = useAppSelector((state) => state.sbu.all_SBUs);
  const allSBGs = useAppSelector((state) => state.sbg.all_SBGs);
  const allCSGOs = useAppSelector((state) => state.csgo.all_CSGOs);
  const isUserHierarchiesFetching = useAppSelector(
    (state) => state.UserHierarchy.isUserHierarchiesFetching
  );

  //To fetch all UserHierarchy, users
  useEffect(() => {
    dispatch(fetchUserHierarchys());
    dispatch(fetchUsers());
    dispatch(fetchSBUs());
    dispatch(fetchSBGs());
    dispatch(fetchCSGOs());
  }, [dispatch]);

  const columns = useMemo(() => {
    return [
      {
        id: "bdpoName",
        numeric: false,
        disablePadding: false,
        label: "BDPOC",
      },
      {
        id: "bdheadName",
        numeric: false,
        disablePadding: false,
        label: "BD Head",
      },
      {
        id: "sbuName",
        numeric: false,
        disablePadding: false,
        label: "SBU",
      },
      {
        id: "sbgName",
        numeric: false,
        disablePadding: false,
        label: "SBG",
      },
      {
        id: "csgoName",
        numeric: false,
        disablePadding: false,
        label: "CSGO",
      },
      {
        id: "createdDate",
        numeric: false,
        disablePadding: false,
        label: "Created Date",
        width: 150,
      },
      // {
      //   id: "status",
      //   numeric: false,
      //   disablePadding: false,
      //   label: "Status",
      // },
    ];
  }, []);

  useEffect(() => {
    setRootData(() =>
      allUserHierarchys.map((hierarchy) => {
        const bdpoUser = allUsers.find(
          (rec) => rec.userId === hierarchy.bdpoCuserId
        );
        const bdpoUserName = bdpoUser
          ? `${bdpoUser.firstName} ${bdpoUser.lastName}`
          : "NA";

        const bdHeadUser = allUsers.find(
          (rec) => rec.userId === hierarchy.bdheadUserId
        );
        const bdHeadUserName = bdHeadUser
          ? `${bdHeadUser.firstName} ${bdHeadUser.lastName}`
          : "NA";

        const SBUUser = allSBUs.find((sbu) => sbu.sbuid === hierarchy.sbuid);
        const SBUUserName = SBUUser ? SBUUser.name : "NA";

        const SBGUser = allSBGs.find((sbg) => sbg.sbgid === hierarchy.sbgid);
        const SBGUserName = SBGUser ? SBGUser.name : "NA";

        const CSGORec = allCSGOs.find(
          (csgo) => csgo.csgoid === hierarchy.csgoid
        );
        const CSGOName = CSGORec ? CSGORec.name : "NA";

        return {
          id: `${hierarchy.bdpoCuserId}|${hierarchy.bdheadUserId}|${hierarchy.sbuid}|${hierarchy.sbgid}|${hierarchy.csgoid}`,
          bdpoName: bdpoUserName,
          bdheadName: bdHeadUserName,
          sbuName: SBUUserName,
          sbgName: SBGUserName,
          csgoName: CSGOName,
          createdDate: displayDateFormart(hierarchy.createdDate),
          // status: hierarchy.isActive ? "Active" : "Inactive",
        };
      })
    );
  }, [allUserHierarchys, allUsers, allSBUs, allSBGs, allCSGOs]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const idArr = id.toString().split("|");
    const editRec = allUserHierarchys.find(
      (rec) =>
        rec.bdpoCuserId === +idArr[0] &&
        rec.bdheadUserId === +idArr[1] &&
        rec.sbuid === +idArr[2] &&
        rec.sbgid === +idArr[3] &&
        rec.csgoid === +idArr[4]
    );
    setMode("delete");
    setEditData(editRec);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {!isValidArr(allUserHierarchys) && isUserHierarchiesFetching ? (
        <Loader />
      ) : null}
      <PageTitle
        title="User Hierarchy"
        btnText="Add User Hierarchy"
        onClick={createHandler}
      />
      <PageSearch
        placeholder="Search User Hierarchys"
        searchHandler={searchHandler}
      />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
        isEditable={false}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={
            mode === "add" ? "Create User Hierarchy" : "Delete User Hierarchy"
          }
          handleModalClose={handleModalClose}
        >
          <UserHierarchyForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default UserHierarchyPage;
